import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Il Modulo di components/verifica/ModuliCampi.js (verifica/_modulo): l'editor di una parte di un file dei moduli.
// - Lo stato di React è nei campi: ogni parte manda il suo stato di partenza in JSON (data-modulo-sempre) e solo i
//   valori che l'operatore ha cambiato (data-cambiato), così il corpo del PUT è quello di React
//   (VerificaPayload.modulo_payload) e ogni proprietà letta (acro, id, riga, …) resta.
// - Quello che React mostrava o toglieva secondo tipo, firmatario, applicabilità e domanda della condizione si mostra
//   e si nasconde qui (data-modulo-se), così come il testo della cella del dato di ogni campo.
// - «Aggiungi campo» e «Conferma il modulo» controllano etichetta (200) e citazione (600) prima di mandare
//   (ModuliCampi.js:66-68, 87-91); «Leggi i campi» e «Conferma il modulo» vanno con fetch e la risposta è un
//   turbo-stream (il file riletto, oppure l'avviso «Modulo non salvato. …»); durante la richiesta i pulsanti del
//   modulo sono disattivati (busy).
const FIRME = { digitale: "firma digitale", autografa: "firma autografa", digitale_o_autografa: "firma digitale oppure autografa con copia del documento" }
const GENERICO = "Si è verificato un errore. Riprova."

export default class ModuloController extends Controller {
  // Quando un'azione riscrive il file, l'editor di una parte aperta tiene quello che useEffect([u]) di React non
  // rifaceva: applicabilità e condizione (il riquadro passa nel nuovo editor con i suoi campi cambiati), «Stesso modulo
  // di», il campo da aggiungere e l'avviso. Tipo, firmatario, campi, fasi e piattaforma tornano quelli della parte.
  static istantanea(editor) {
    const stato = JSON.parse(editor.querySelector("[data-modulo-target~='stato']").value)
    const scelto = (el, iniziale) => (el && el.dataset.cambiato !== undefined ? el.value : iniziale)
    const appl = [...editor.querySelectorAll("[data-modulo-target~='appl']")].find((r) => r.checked)?.value
    const errore = editor.querySelector("[data-modulo-target~='errore']")
    return {
      condizioni: editor.querySelector("[data-modulo-condizioni]"),
      applicabilita: appl || stato.applicabilita,
      stessoDi: scelto(editor.querySelector("[data-modulo-target~='stessoDi']"), stato.stesso_di),
      nuovo: ["nuovoTipo", "nuovaEtichetta", "nuovaFrase"].map((t) => editor.querySelector(`[data-modulo-target~='${t}']`).value),
      errore: errore.hidden ? "" : errore.textContent,
    }
  }

  static ripristina(editor, ist) {
    editor.querySelector("[data-modulo-condizioni]").replaceWith(ist.condizioni)
    const campoStato = editor.querySelector("[data-modulo-target~='stato']")
    campoStato.value = JSON.stringify({ ...JSON.parse(campoStato.value), applicabilita: ist.applicabilita, stesso_di: ist.stessoDi })
    const menu = editor.querySelector("[data-modulo-target~='stessoDi']")
    if (menu) menu.value = ist.stessoDi
    ;["nuovoTipo", "nuovaEtichetta", "nuovaFrase"].forEach((t, i) => { editor.querySelector(`[data-modulo-target~='${t}']`).value = ist.nuovo[i] })
    const errore = editor.querySelector("[data-modulo-target~='errore']")
    ModuloController.scriviErrore(errore, ist.errore, editor.dataset.moduloKeyValue)
  }

  static scriviErrore(p, testo, key) {
    p.textContent = testo
    p.hidden = !testo
    if (testo) {
      p.setAttribute("role", "alert")
      p.dataset.testid = `modulo-errore-${key}`
    } else {
      p.removeAttribute("role")
      delete p.dataset.testid
    }
  }

  static targets = ["stato", "tipo", "soggetto", "stessoDi", "pfNome", "fasi", "fase", "aggiungiFase", "modelloFase", "campi", "campo",
    "modelloCampo", "nuovoTipo", "nuovaEtichetta", "nuovaFrase", "condizione", "dimensioni", "appl", "condDato", "errore", "pulsante"]
  static values = { key: String, doc: String, letto: Boolean, urlConferma: String, urlLeggi: String }

  connect() {
    this.prossimo = Date.now()
    this.aggiorna()
  }

  // --- stato --------------------------------------------------------------------------------------------------

  // un campo con un nome cambiato dall'operatore va nel corpo (i radio dell'applicabilità hanno il loro valore)
  cambia(event) {
    const el = event.target
    if (el.name && el.type !== "radio" && !el.hasAttribute("data-modulo-sempre")) el.dataset.cambiato = ""
    this.aggiorna()
  }

  json(el) {
    try { return JSON.parse(el.value) } catch { return {} }
  }

  // il valore di un menu: quello scelto, oppure lo stato di partenza (anche un valore che il menu non ha)
  scelto(el, iniziale) {
    return el && el.dataset.cambiato !== undefined ? el.value : iniziale
  }

  get stato() {
    return this.json(this.statoTarget)
  }

  get tipo() { return this.scelto(this.tipoTarget, this.stato.tipo) }

  get soggetto() { return this.scelto(this.soggettoTarget, this.stato.soggetto) }

  get stessoDi() { return this.hasStessoDiTarget ? this.scelto(this.stessoDiTarget, this.stato.stesso_di) : this.stato.stesso_di }

  get applicabilita() { return this.applTargets.find((r) => r.checked)?.value || this.stato.applicabilita }

  get condDato() { return this.scelto(this.condDatoTarget, this.json(this.condizioneTarget).dato) }

  // --- vista --------------------------------------------------------------------------------------------------

  aggiorna() {
    const tipo = this.tipo
    const corpo = this.lettoValue || tipo === "facsimile"
    const impresa = this.soggetto === "impresa"
    const dato = this.condDato
    const se = {
      "senza-lettura": tipo !== "facsimile",
      corpo,
      "stesso-di": corpo && !!this.stessoDi,
      piattaforma: corpo && ["piattaforma", "facsimile"].includes(tipo),
      facsimile: tipo === "facsimile",
      "non-facsimile": tipo !== "facsimile",
      esterno: corpo && !impresa,
      campi: corpo && tipo !== "facsimile",
      condizionale: this.applicabilita === "condizionale",
      altro: dato === "altro",
      dimensioni: dato === "dimensione_impresa",
      "si-applica": dato !== "dimensione_impresa",
    }
    this.element.querySelectorAll("[data-modulo-se]").forEach((el) => {
      if (el.closest("template")) return
      el.hidden = !se[el.dataset.moduloSe]
    })
    if (this.hasPfNomeTarget) this.pfNomeTarget.placeholder = tipo === "facsimile" ? "es. Invitalia" : "es. GeCoWEB Plus"
    this.campoTargets.forEach((riga) => this.aggiornaCampo(riga, impresa))
    this.aggiornaFasi()
  }

  // la cella del dato: a cura di un altro soggetto, il dato dell'impresa (campo da compilare), oppure il testo
  aggiornaCampo(riga, impresa) {
    const tipo = riga.dataset.tipo
    const aCura = riga.querySelector("[data-variante='a-cura']")
    const menu = riga.querySelector("[data-variante='dato']")
    const testo = riga.querySelector("[data-variante='testo']")
    if (menu) menu.hidden = !!aCura || !impresa
    testo.hidden = !!aCura || (tipo === "testo" && impresa)
    const modo = riga.querySelector("[data-campo-modo]")?.value
    testo.textContent = tipo === "firma" ? (FIRME[modo] || "modalità di firma non indicata")
      : tipo === "testo" ? "dato del soggetto esterno" : !impresa ? "le compila il soggetto esterno" : "decide il cliente"
  }

  // le fasi: testid in ordine, il cestino con più di una fase, «+ Aggiungi una fase» con meno di tre
  aggiornaFasi() {
    const fasi = this.faseTargets
    fasi.forEach((riga, k) => {
      riga.querySelectorAll("[data-modulo-numero]").forEach((el) => { el.dataset.testid = `${el.dataset.moduloNumero}${k}` })
      riga.querySelector("[data-togli-fase]").hidden = fasi.length <= 1
      riga.querySelector("[data-segnaposto-fase]").hidden = fasi.length > 1
    })
    this.aggiungiFaseTarget.hidden = fasi.length >= 3
  }

  etichetta(event) {
    const riga = event.target.closest("tr")
    riga.querySelector("[data-campo-frase]").setAttribute("aria-label", `Frase del modulo: ${event.target.value}`)
  }

  // --- modifiche ----------------------------------------------------------------------------------------------

  facsimile() {
    this.tipoTarget.value = "facsimile"
    this.tipoTarget.dataset.cambiato = ""
    this.aggiorna()
  }

  dellImpresa(event) {
    const cella = event.target.closest("td")
    cella.querySelector("[data-campo-a-cura]").dataset.cambiato = ""
    cella.querySelector("[data-variante='a-cura']").remove()
    this.aggiorna()
  }

  dimensione(event) {
    const scelte = this.json(this.dimensioniTarget)
    const k = event.target.value
    const dopo = event.target.checked ? [...scelte, k] : scelte.filter((x) => x !== k)
    this.dimensioniTarget.value = JSON.stringify(dopo)
    this.dimensioniTarget.dataset.cambiato = ""
  }

  aggiungiFase() {
    const n = this.prossimo++
    const html = this.modelloFaseTarget.innerHTML.replaceAll("__N__", String(n)).replaceAll("__K__", String(this.faseTargets.length))
    this.aggiungiFaseTarget.insertAdjacentHTML("beforebegin", html)
    this.aggiorna()
  }

  togliFase(event) {
    event.target.closest("[data-modulo-target~='fase']").remove()
    this.aggiorna()
  }

  togliCampo(event) {
    event.target.closest("tr").remove()
  }

  aggiungiCampo() {
    const etichetta = this.nuovaEtichettaTarget.value
    const frase = this.nuovaFraseTarget.value
    let messaggio = ""
    if (!etichetta.trim() || frase.trim().length < 10)
      messaggio = "Campo non aggiunto. Scrivi l'etichetta e copia la frase del modulo (almeno 10 caratteri)."
    else if (etichetta.trim().length > 200 || frase.trim().length > 600)
      messaggio = "Campo non aggiunto. Etichetta massimo 200 caratteri, citazione massimo 600: copia un passaggio più breve dello stesso documento."
    if (messaggio) { this.mostraErrore(messaggio); window.toast?.error(messaggio); return }
    this.mostraErrore("")
    const tipo = this.nuovoTipoTarget.value
    const campo = { id: `n${Date.now()}`, tipo, etichetta: etichetta.trim(), frase: frase.trim(), sezione: "", dato: "", acro: "", non_applicabile: "" }
    const modello = this.modelloCampoTargets.find((t) => t.dataset.tipo === tipo)
    const html = modello.innerHTML.replaceAll("__ID__", campo.id).replaceAll("__N__", String(this.prossimo++))
    const contenitore = document.createElement("template")
    contenitore.innerHTML = html
    const riga = contenitore.content.querySelector("tr")
    riga.querySelector("[data-campo-json]").value = JSON.stringify(campo)
    riga.querySelector("[data-campo-etichetta]").value = campo.etichetta
    const fraseEl = riga.querySelector("[data-campo-frase]")
    fraseEl.value = campo.frase
    fraseEl.setAttribute("aria-label", `Frase del modulo: ${campo.etichetta}`)
    this.campiTarget.appendChild(riga)
    this.nuovoTipoTarget.value = "testo"
    this.nuovaEtichettaTarget.value = ""
    this.nuovaFraseTarget.value = ""
    this.aggiorna()
  }

  // --- avviso -------------------------------------------------------------------------------------------------

  mostraErrore(testo) {
    ModuloController.scriviErrore(this.erroreTarget, testo, this.keyValue)
  }

  // --- azioni -------------------------------------------------------------------------------------------------

  leggi(event) {
    this.invia(event.currentTarget, this.urlLeggiValue, { method: "POST" }, () => window.toast?.error(GENERICO))
  }

  conferma(event) {
    const pulsante = event.currentTarget
    this.mostraErrore("")
    if (this.tipo !== "facsimile") {
      const invalido = this.campoTargets.map((riga) => this.campoAttuale(riga))
        .find((c) => c.etichetta.trim().length > 200 || c.frase.trim().length > 600)
      if (invalido) {
        this.mostraErrore(`Modulo non salvato. Controlla il campo «${invalido.etichetta.slice(0, 200)}»: etichetta massimo 200 caratteri, citazione massimo 600. Copia un passaggio più breve dello stesso documento.`)
        return
      }
    }
    this.invia(pulsante, this.urlConfermaValue, { method: "PUT", body: this.corpo() }, () => {
      const messaggio = `Modulo non salvato. ${GENERICO}`
      this.mostraErrore(messaggio)
      window.toast?.error(messaggio)
    })
  }

  // etichetta e frase di un campo come nello stato di React (la frase cambiata, altrimenti quella del campo)
  campoAttuale(riga) {
    const c = this.json(riga.querySelector("[data-campo-json]"))
    const etichetta = riga.querySelector("[data-campo-etichetta]")
    const frase = riga.querySelector("[data-campo-frase]")
    return {
      etichetta: String(etichetta.dataset.cambiato !== undefined ? etichetta.value : (c.etichetta ?? "")),
      frase: String(frase.dataset.cambiato !== undefined ? frase.value : (c.frase ?? "")),
    }
  }

  // il corpo del form: gli stati di partenza e i valori cambiati, più l'applicabilità scelta
  corpo() {
    const dati = new URLSearchParams()
    this.element.querySelectorAll("[name]").forEach((el) => {
      if (el.type === "radio" || el.disabled) return
      if (el.hasAttribute("data-modulo-sempre") || el.dataset.cambiato !== undefined) dati.append(el.name, el.value)
    })
    dati.append("applicabilita", this.applicabilita)
    return dati
  }

  // busy del modulo: Leggi, Fac-simile e Conferma disattivati, l'icona che gira sul pulsante premuto
  async invia(pulsante, url, { method, body }, senzaRisposta) {
    const pulsanti = this.pulsanteTargets.filter((b) => !b.disabled)
    pulsanti.forEach((b) => { b.disabled = true })
    this.attesa(pulsante, true)
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const risposta = await fetch(url, {
        method, body, credentials: "same-origin",
        headers: { Accept: "text/vnd.turbo-stream.html", ...(token ? { "X-CSRF-Token": token } : {}) },
      })
      // una risposta rediretta (sessione scaduta) o senza turbo-stream non ha un dettaglio da mostrare
      if (risposta.redirected || !(risposta.headers.get("Content-Type") || "").includes("turbo-stream")) throw new Error("risposta")
      Turbo.renderStreamMessage(await risposta.text())
    } catch {
      senzaRisposta()
    } finally {
      pulsanti.forEach((b) => { if (b.isConnected) b.disabled = false })
      if (pulsante.isConnected) this.attesa(pulsante, false)
    }
  }

  attesa(el, si) {
    el.querySelectorAll(":scope > [data-attesa]").forEach((x) => { x.hidden = !si })
  }
}
